import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { clientesAPI } from '../utils/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Save, Users } from 'lucide-react';
import Swal from 'sweetalert2';

export const ClienteForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  usePageTitle(isEdit ? 'Editar Cliente' : 'Novo Cliente');

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [formData, setFormData] = useState({
    nome: '',
    morada: '',
    cidade: '',
    codigo_postal: '',
    email: '',
    telemovel: '',
    data_nascimento: '',
    nif: '',
    id_especial: '',
    observacoes: ''
  });

  useEffect(() => {
    if (isEdit) {
      fetchCliente();
    }
  }, [id]);

  const fetchCliente = async () => {
    try {
      const response = await clientesAPI.getOne(id);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching cliente:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Cliente não encontrado.',
        confirmButtonColor: '#0D9488'
      });
      navigate('/clientes');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Campo obrigatório',
        text: 'O nome do cliente é obrigatório.',
        confirmButtonColor: '#0D9488'
      });
      return;
    }

    setLoading(true);
    try {
      if (isEdit) {
        await clientesAPI.update(id, formData);
        Swal.fire({
          icon: 'success',
          title: 'Atualizado!',
          text: 'Cliente atualizado com sucesso.',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        await clientesAPI.create(formData);
        Swal.fire({
          icon: 'success',
          title: 'Criado!',
          text: 'Cliente criado com sucesso.',
          timer: 1500,
          showConfirmButton: false
        });
      }
      navigate('/clientes');
    } catch (error) {
      console.error('Error saving cliente:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível guardar o cliente.',
        confirmButtonColor: '#0D9488'
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cliente-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/clientes">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            {isEdit ? 'Editar Cliente' : 'Novo Cliente'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isEdit ? 'Atualize os dados do cliente' : 'Preencha os dados do novo cliente'}
          </p>
        </div>
      </div>

      {/* Form */}
      <Card className="border-slate-200 max-w-3xl">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Users className="h-5 w-5 text-amber-600" />
            Dados do Cliente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="nome" className="text-slate-700">
                Nome <span className="text-red-500">*</span>
              </Label>
              <Input
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                placeholder="Nome completo do cliente"
                data-testid="cliente-nome-input"
              />
            </div>

            {/* ID Especial */}
            <div className="space-y-2">
              <Label htmlFor="id_especial" className="text-slate-700">
                ID Especial
              </Label>
              <Input
                id="id_especial"
                name="id_especial"
                value={formData.id_especial}
                onChange={handleChange}
                placeholder="Identificador especial (ex: CL001)"
                data-testid="cliente-id-especial-input"
              />
              <p className="text-xs text-slate-500">
                Identificador único para facilitar a pesquisa ao criar receitas
              </p>
            </div>

            {/* Contact Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="email@exemplo.pt"
                  data-testid="cliente-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telemovel" className="text-slate-700">Telemóvel</Label>
                <Input
                  id="telemovel"
                  name="telemovel"
                  value={formData.telemovel}
                  onChange={handleChange}
                  placeholder="912345678"
                  data-testid="cliente-telemovel-input"
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="morada" className="text-slate-700">Morada</Label>
              <Input
                id="morada"
                name="morada"
                value={formData.morada}
                onChange={handleChange}
                placeholder="Rua, número, andar..."
                data-testid="cliente-morada-input"
              />
            </div>

            {/* City Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cidade" className="text-slate-700">Cidade</Label>
                <Input
                  id="cidade"
                  name="cidade"
                  value={formData.cidade}
                  onChange={handleChange}
                  placeholder="Lisboa"
                  data-testid="cliente-cidade-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="codigo_postal" className="text-slate-700">Código Postal</Label>
                <Input
                  id="codigo_postal"
                  name="codigo_postal"
                  value={formData.codigo_postal}
                  onChange={handleChange}
                  placeholder="1000-001"
                  data-testid="cliente-codigo-postal-input"
                />
              </div>
            </div>

            {/* NIF and Birth Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nif" className="text-slate-700">NIF</Label>
                <Input
                  id="nif"
                  name="nif"
                  value={formData.nif}
                  onChange={handleChange}
                  placeholder="123456789"
                  data-testid="cliente-nif-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data_nascimento" className="text-slate-700">Data de Nascimento</Label>
                <Input
                  id="data_nascimento"
                  name="data_nascimento"
                  type="date"
                  value={formData.data_nascimento}
                  onChange={handleChange}
                  data-testid="cliente-data-nascimento-input"
                />
              </div>
            </div>

            {/* Observações */}
            <div className="space-y-2">
              <Label htmlFor="observacoes" className="text-slate-700">Observações</Label>
              <Textarea
                id="observacoes"
                name="observacoes"
                value={formData.observacoes || ''}
                onChange={handleChange}
                placeholder="Notas ou observações sobre o cliente..."
                rows={3}
                data-testid="cliente-observacoes-input"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4 pt-4">
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                disabled={loading}
                data-testid="cliente-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    A guardar...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {isEdit ? 'Atualizar' : 'Criar'} Cliente
                  </>
                )}
              </Button>
              <Link to="/clientes">
                <Button type="button" variant="outline">
                  Cancelar
                </Button>
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
